import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";

interface UseFpsLimitProps {
  enabled: boolean;
  maxFps: number;
}

const SECONDS_IN_MS = 1000;

export const useFpsLimit = ({ enabled, maxFps }: UseFpsLimitProps) => {
  const get = useThree((s) => s.get);
  const set = useThree((s) => s.set);

  const originalInvalidateRef = useRef<
    ReturnType<typeof get>["invalidate"] | null
  >(null);
  const rafIdRef = useRef<number | null>(null);
  const lastRef = useRef(0);
  const accRef = useRef(0);

  useEffect(() => {
    const state = get();

    if (!enabled || maxFps <= 0) {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (originalInvalidateRef.current) {
        set({ invalidate: originalInvalidateRef.current });
      }
      state.invalidate();
      lastRef.current = 0;
      accRef.current = 0;
      return;
    }

    if (!originalInvalidateRef.current) {
      originalInvalidateRef.current = state.invalidate;
    }

    // Strict cap: swallow external invalidates; the ticker will render on schedule.
    set({
      invalidate: () => {
        // no-op; frames are driven by the ticker
      },
    });

    const step = SECONDS_IN_MS / maxFps;

    const tick = (now: number) => {
      if (lastRef.current === 0) {
        lastRef.current = now;
      }
      const dt = now - lastRef.current;
      lastRef.current = now;
      accRef.current += dt;

      if (accRef.current >= step) {
        accRef.current %= step;
        originalInvalidateRef.current?.(1);
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    // Kick off immediately
    originalInvalidateRef.current?.(1);
    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (originalInvalidateRef.current) {
        set({ invalidate: originalInvalidateRef.current });
      }
      lastRef.current = 0;
      accRef.current = 0;
    };
  }, [enabled, maxFps, get, set]);
};
